/**
 * The parked-car field. Owned by `traffic`.
 *
 * WHY THIS EXISTS
 *   reference/REFERENCE.md §6.9 — "Bumper to bumper, both sides, continuously, with no gaps
 *   … if our streets have empty kerbs they will read as a film set." The round-2 blind
 *   critics failed `street-speed` criterion 7 outright and called the empty kerb the single
 *   biggest missing Montréal signifier in `wide-establish`. Measured on the round-2 build:
 *   two parked cars in the whole `street-speed` frame, both on the right kerb, against bays
 *   that could hold about twenty.
 *
 * HOW IT PACKS
 *   Not on a fixed pitch. A fixed pitch with a probability gate is exactly what produced the
 *   dotted line we are replacing. This walks a cursor along each kerb, places a car of a
 *   randomly drawn LENGTH, then advances by that car's own length plus a small bumper gap.
 *   The result is a continuous wall of metal whose gaps are sub-metre by construction, which
 *   is the falsifiable thing criterion 7 measures ("no gap longer than one car length").
 *
 * WHAT IT IS NOT
 *   It is not a second, competing line of cars. `world` places its own parked props (§3
 *   gives it street furniture) and this file must never double up on them. Every candidate
 *   slot is probed with `ctx.world.collide()` first, so a slot already holding a world car —
 *   or a hydrant, a bin, a tree, a pole or a snowbank — is skipped and the cursor steps past
 *   it. The two layers interleave into one continuous line, and if `world` changes its own
 *   density this file silently fills whatever is left rather than fighting it.
 *
 * COST MODEL — this is the part that must not regress
 *   · Built ONCE, in init(). `fixedUpdate` never touches the layout. Zero per-step CPU
 *     beyond one optional hash-grid probe around the truck.
 *   · One InstancedMesh per occupied `tile`-metre square, so the frustum culls a whole block
 *     in a single test and off-screen streets cost nothing at all.
 *   · 30 triangles per car (models.js `buildParkedCar`) and a baked contact shadow instead
 *     of a shadow-map pass.
 *   · One shared geometry and the rig's EXISTING paint material, so the field adds no shader
 *     program and only as many draw calls as there are visible tiles.
 *
 * DETERMINISM (§6). Layout is a pure function of the lane graph plus a seeded fork, walked in
 * a fixed order. No wall clock, no Math.random, no dependence on frame timing.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';

const T = TUNING;
const K = T.parked;

export class ParkedField {
  /**
   * @param {object} ctx                 game context
   * @param {LaneGraph} graph            the same graph the AI drives
   * @param {Rng} rng                    a fork of the traffic fork — layout must not perturb spawning
   * @param {{body:THREE.BufferGeometry, detailNear:THREE.BufferGeometry,
   *          detailFar:THREE.BufferGeometry}} geo   the three parked-car geometries
   * @param {THREE.Material} material    the rig's paint clone (vertexColors + instanceColor)
   * @param {THREE.Material} detailMat   the rig's tyre clone — untinted, so its vertex colours
   *                                     are absolute albedos (blob, glass, wheels, lenses)
   * @param {number[][]} paints          linear rgb triples
   */
  constructor(ctx, graph, rng, geo, material, detailMat, paints) {
    this.ctx = ctx;
    this.graph = graph;
    this.rng = rng;
    this.geo = geo;
    this.material = material;
    this.detailMat = detailMat;
    this.paints = paints;
    this._paintCdf = this._buildPaintCdf();

    this.group = new THREE.Group();
    this.group.name = 'traffic.parked';
    this.group.matrixAutoUpdate = false;
    ctx.scene.add(this.group);

    this.meshes = [];
    /** Per-tile LOD records: {near, far, cx, cz}. One distance test each, per frame. */
    this.tiles = [];
    this._lodNear = 0;
    this.count = 0;

    /** Flat arrays for the truck-vs-parked broadphase. Allocated once in build(). */
    this.px = null; this.pz = null; this.pcos = null; this.psin = null;
    this.phl = null; this.phw = null; this.pcool = null;
    this._cellMap = null;
    this.lastPenetration = 0;

    this.stats = { placed: 0, blocked: 0, holeSkip: 0, offRoad: 0, tiles: 0, probes: 0 };
  }

  /* ══════════════════════════════════════════════════════════════════ build ══ */

  build() {
    if (!K.enabled) return;
    const g = this.graph;
    if (!g || g.edgeCount === 0) return;

    const world = this.ctx.world;
    const rng = this.rng;
    const holes = this._holeList();

    // Layout runs once, so plain arrays are fine here. N9 governs the per-frame hot paths and
    // this is emphatically not one of them.
    const buckets = new Map();
    const cars = [];
    const probe = { x: 0, y: 0, z: 0, r: K.probeRadius };

    // ONE directed edge per physical segment: the graph stores both directions, and walking
    // both would lay two cars in every bay.
    for (let ei = 0; ei < g.edges.length && cars.length < K.maxCars; ei++) {
      const e = g.edges[ei];
      if (e.rev >= 0 && e.rev < ei) continue;
      if (e.halfW < K.minHalfWidth) continue;      // a lane-and-a-half street has no bay

      const lat0 = e.halfW - K.offset;
      const sEnd = e.len - K.endClear;
      if (sEnd <= K.endClear) continue;

      for (let side = -1; side <= 1; side += 2) {
        // A car on the right of a direction of travel faces that direction; the far kerb
        // faces the other way. That is what makes both kerbs read as one-way parking.
        const baseYaw = side > 0 ? Math.atan2(e.dx, e.dz) : Math.atan2(-e.dx, -e.dz);

        let s = K.endClear;
        while (s < sEnd && cars.length < K.maxCars) {
          const sl = rng.range(K.scaleLen[0], K.scaleLen[1]);
          const len = K.body.L * sl;
          const cs = s + len * 0.5;
          if (cs + len * 0.5 > sEnd) break;

          const off = side * (lat0 + rng.spread(K.jitterLat));
          const x = e.ax + e.dx * cs + e.rx * off;
          const z = e.az + e.dz * cs + e.rz * off;

          if (this._nearHole(holes, x, z)) {
            this.stats.holeSkip++;
            s += K.blockedStep;
            continue;
          }

          const ground = world.sampleGround(x, z);
          if (ground.surface !== 'road') {
            this.stats.offRoad++;
            s += K.blockedStep;
            continue;
          }

          probe.x = x; probe.y = ground.y + K.probeY; probe.z = z;
          this.stats.probes++;
          const hits = world.collide(probe);
          if (hits && hits.length > 0) {
            this.stats.blocked++;
            s += K.blockedStep;
            continue;
          }

          cars.push({
            x, y: ground.y, z,
            yaw: baseYaw + rng.spread(K.jitterYaw),
            sl,
            sh: rng.range(K.scaleHeight[0], K.scaleHeight[1]),
            sw: rng.range(K.scaleWidth[0], K.scaleWidth[1]),
            col: this._pickPaint(rng),
          });

          const idx = cars.length - 1;
          const tx = Math.floor(x / K.tile), tz = Math.floor(z / K.tile);
          const key = tx * 10007 + tz;
          let b = buckets.get(key);
          if (!b) { b = []; buckets.set(key, b); }
          if (b.length < K.maxPerTile) b.push(idx);

          s = cs + len * 0.5 + K.gap + rng.range(0, K.gapJitter);
        }
      }
    }

    this._buildMeshes(cars, buckets);
    this._buildBroadphase(cars);
    this.count = cars.length;
    this.stats.placed = cars.length;
    this.stats.tiles = this.tiles.length;
  }

  /**
   * Nobody parks in the work zone. `gameplay` initialises AFTER `traffic`, so its pothole
   * list is still the empty fallback at this point — we read `world`'s candidate spawn
   * points instead, which is a superset and therefore the conservative choice.
   */
  _holeList() {
    const out = [];
    const pts = this.ctx.world.spawnPoints('pothole');
    if (!pts) return out;
    for (const p of pts) {
      if (!p || !p.pos) continue;
      out.push(p.pos.x, p.pos.z);
    }
    return out;
  }

  _nearHole(list, x, z) {
    const r2 = K.holeClear * K.holeClear;
    for (let i = 0; i < list.length; i += 2) {
      const dx = x - list[i], dz = z - list[i + 1];
      if (dx * dx + dz * dz < r2) return true;
    }
    return false;
  }

  /** Weighted paint draw. Uniform picking is what put a full-strength white car in the
   *  foreground-right corner of `street-speed` one time in eight. */
  _buildPaintCdf() {
    const w = K.paintWeights;
    const out = new Float64Array(this.paints.length);
    let sum = 0;
    for (let i = 0; i < out.length; i++) { sum += (w && w[i] != null) ? w[i] : 1; out[i] = sum; }
    for (let i = 0; i < out.length; i++) out[i] /= sum;
    return out;
  }

  _pickPaint(rng) {
    const u = rng.next();
    const cdf = this._paintCdf;
    let i = 0;
    while (i < cdf.length - 1 && u > cdf[i]) i++;
    const c = this.paints[i], s = K.paintScale;
    return [c[0] * s, c[1] * s, c[2] * s];
  }

  /**
   * Three InstancedMeshes per tile sharing one instance matrix set:
   *   · the painted shell (paint material, per-instance colour) — always drawn, casts
   *   · the near detail   (tyre material, untinted)             — drawn inside `lodNear`
   *   · the far detail    (tyre material, untinted)             — drawn outside it
   *
   * `castShadow` is now TRUE on the shell. It was false with a comment promising a baked
   * contact shadow that nobody could find in the frame; the shadow map is ±52 m around the
   * player, so only the near field ever pays for it, and that is exactly the field where a
   * missing shadow is the thing a critic notices first.
   */
  _buildMeshes(cars, buckets) {
    const dummy = new THREE.Object3D();
    dummy.rotation.order = 'YXZ';

    for (const [key, list] of buckets) {
      if (list.length === 0) continue;
      const n = list.length;

      const shell = new THREE.InstancedMesh(this.geo.body, this.material, n);
      shell.name = 'traffic.parked.' + key;
      shell.castShadow = true;
      shell.receiveShadow = true;
      shell.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);

      // Both detail meshes carry an all-ones instanceColor they never use. Three keys its
      // shader programs on the `USE_INSTANCING_COLOR` define, so a mesh WITHOUT the attribute
      // sharing a material with the wheel/blob meshes that have it compiles a second program
      // for nothing. n×3 floats is a great deal cheaper than a program slot.
      const white = () => {
        const a = new THREE.InstancedBufferAttribute(new Float32Array(n * 3).fill(1), 3);
        a.needsUpdate = true;
        return a;
      };

      const near = new THREE.InstancedMesh(this.geo.detailNear, this.detailMat, n);
      near.name = 'traffic.parked.detail.' + key;
      near.instanceColor = white();
      // The blob must never receive the shadow map: a contact shadow inside a cast shadow
      // disappears, and the whole point of the blob is that its value is predictable.
      near.castShadow = false;
      near.receiveShadow = false;
      near.visible = false;

      const far = new THREE.InstancedMesh(this.geo.detailFar, this.detailMat, n);
      far.name = 'traffic.parked.detailFar.' + key;
      far.instanceColor = white();
      far.castShadow = false;
      far.receiveShadow = false;

      let sx = 0, sz = 0;
      for (let i = 0; i < n; i++) {
        const c = cars[list[i]];
        dummy.position.set(c.x, c.y, c.z);
        dummy.rotation.set(0, c.yaw, 0);
        dummy.scale.set(c.sw, c.sh, c.sl);
        dummy.updateMatrix();
        shell.setMatrixAt(i, dummy.matrix);
        near.setMatrixAt(i, dummy.matrix);
        far.setMatrixAt(i, dummy.matrix);
        shell.instanceColor.array[i * 3] = c.col[0];
        shell.instanceColor.array[i * 3 + 1] = c.col[1];
        shell.instanceColor.array[i * 3 + 2] = c.col[2];
        sx += c.x; sz += c.z;
      }
      for (const mesh of [shell, near, far]) {
        mesh.frustumCulled = true;                // the entire point of tiling
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
        this.group.add(mesh);
        this.meshes.push(mesh);
      }
      shell.instanceColor.needsUpdate = true;
      this.tiles.push({ near, far, cx: sx / n, cz: sz / n });
    }
  }

  /**
   * LOD swap. One squared-distance test per tile per frame, no allocation, and a pure
   * function of the camera pose — so a frozen shot is bit-identical (§7.3).
   */
  updateLod(camX, camZ) {
    const r2 = this._lodNear || (this._lodNear = K.lodNear * K.lodNear);
    const tiles = this.tiles;
    for (let i = 0; i < tiles.length; i++) {
      const t = tiles[i];
      const dx = t.cx - camX, dz = t.cz - camZ;
      const isNear = dx * dx + dz * dz < r2;
      t.near.visible = isNear;
      t.far.visible = !isNear;
    }
  }

  /* ═════════════════════════════════════════════════════ truck vs parked car ══ */

  /**
   * A uniform hash grid, built once. `world` registers colliders for its own parked props but
   * knows nothing about ours, so without this the truck drives straight through half the kerb
   * line — a far more obvious bug than the empty kerb we are fixing.
   */
  _buildBroadphase(cars) {
    const n = cars.length;
    this.px = new Float32Array(n);
    this.pz = new Float32Array(n);
    this.pcos = new Float32Array(n);
    this.psin = new Float32Array(n);
    this.phl = new Float32Array(n);
    this.phw = new Float32Array(n);
    this.pcool = new Float32Array(n);

    const map = new Map();
    for (let i = 0; i < n; i++) {
      const c = cars[i];
      this.px[i] = c.x; this.pz[i] = c.z;
      this.pcos[i] = Math.cos(c.yaw); this.psin[i] = Math.sin(c.yaw);
      this.phl[i] = K.body.L * 0.5 * c.sl;
      this.phw[i] = K.body.W * 0.5 * c.sw;
      const key = Math.floor(c.x / K.cell) * 10007 + Math.floor(c.z / K.cell);
      let b = map.get(key);
      if (!b) { b = []; map.set(key, b); }
      b.push(i);
    }
    // Freeze the buckets into typed arrays so the per-step query never walks a JS array of
    // boxed numbers.
    this._cellMap = new Map();
    for (const [key, list] of map) this._cellMap.set(key, Int32Array.from(list));
  }

  /**
   * Nearest parked car overlapping a circle of radius `r` at (x, z), as an oriented-box test.
   * Returns the index, or −1. Only the nine cells around the point are ever visited, so this
   * is a handful of float compares per step regardless of how many cars are parked.
   */
  overlap(x, z, r) {
    this.lastPenetration = 0;
    if (!this._cellMap || this.count === 0) return -1;
    const gx = Math.floor(x / K.cell), gz = Math.floor(z / K.cell);
    let best = -1, bestPen = 0;
    for (let ax = gx - 1; ax <= gx + 1; ax++) {
      for (let az = gz - 1; az <= gz + 1; az++) {
        const list = this._cellMap.get(ax * 10007 + az);
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const i = list[k];
          if (this.pcool[i] > 0) continue;
          const dx = x - this.px[i], dz = z - this.pz[i];
          // Into the car's local frame: +Z forward.
          const lz = dx * this.psin[i] + dz * this.pcos[i];
          const lx = dx * this.pcos[i] - dz * this.psin[i];
          const ox = Math.abs(lx) - this.phw[i];
          const oz = Math.abs(lz) - this.phl[i];
          const cx = ox > 0 ? ox : 0, cz = oz > 0 ? oz : 0;
          const d = Math.sqrt(cx * cx + cz * cz);
          const pen = r - d;
          if (pen > bestPen) { bestPen = pen; best = i; }
        }
      }
    }
    this.lastPenetration = bestPen;
    return best;
  }

  /** Outward normal from parked car `i` toward (x, z). Writes into `out` = [nx, nz]. */
  normalAt(i, x, z, out) {
    const dx = x - this.px[i], dz = z - this.pz[i];
    const lz = dx * this.psin[i] + dz * this.pcos[i];
    const lx = dx * this.pcos[i] - dz * this.psin[i];
    // Push out along whichever local axis is least penetrated — an arcade box response.
    const ox = this.phw[i] - Math.abs(lx);
    const oz = this.phl[i] - Math.abs(lz);
    let nlx = 0, nlz = 0;
    if (ox < oz) nlx = lx >= 0 ? 1 : -1;
    else nlz = lz >= 0 ? 1 : -1;
    out[0] = nlx * this.pcos[i] + nlz * this.psin[i];
    out[1] = -nlx * this.psin[i] + nlz * this.pcos[i];
    return out;
  }

  markHit(i) { if (this.pcool) this.pcool[i] = K.contactCooldown; }

  /** Cooldowns are sparse and the array is small; one linear pass beats bookkeeping. */
  tick(dt) {
    const c = this.pcool;
    if (!c) return;
    for (let i = 0; i < c.length; i++) if (c[i] > 0) c[i] -= dt;
  }

  /* ═════════════════════════════════════════════════════════════════ teardown ══ */

  dispose() {
    for (const m of this.meshes) {
      this.group.remove(m);
      m.dispose();
    }
    this.meshes.length = 0;
    this.tiles.length = 0;
    if (this.group.parent) this.group.parent.remove(this.group);
    this._cellMap = null;
    this.px = this.pz = this.pcos = this.psin = this.phl = this.phw = this.pcool = null;
    this.count = 0;
  }
}

export default ParkedField;
